import {Box, Grid, textAlign} from "@mui/system";
import style from "../../assets/css/proposal.module.scss";
import {Link, useNavigate} from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Breadcrumbs,
  Typography,
} from "@mui/material";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useCallback, useEffect, useMemo, useState} from "react";
import {HiCurrencyDollar} from "react-icons/hi2";
import GlobalDateField from "../global-fields/GlobalDateField";
import GlobalTextField from "../global-fields/GlobalTextField";
import GlobalModal from "../global-fields/GlobalModal";
import {useDispatch, useSelector} from "react-redux";
import {selectAuthState} from "../../store/authSlice";
import {selectDataStoreState, setDataStore} from "../../store/dataSlice";
import {SiAdobeacrobatreader} from "react-icons/si";
import {ERR_MESSAGE, MANDATORY_QUES} from "../../constants/constants";
import GlobalNotificationPopup from "../global-fields/GlobalNotificationPopup";
import {
  normalizeCurrencyToNumber,
  parseToNumber,
  removeCookies,
  USD_FORMATTER,
} from "../../constants/helper";
import {selectUiState, setUiData} from "../../store/uiSlice";
import {
  EXHIBITIONS,
  GET_EXHIBITION_COVERAGE,
  REQUEST_CLAIM,
  REQUEST_EXHIBITION,
} from "../../constants/apiConstant";
import dayjs from "dayjs";
import GlobalRadio from "../global-fields/GlobalRadio";

const ExhibitionFormModal = ({fetchListAgain}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {user} = useSelector(selectAuthState);
  const {exhibitionParentProposal, disableExhibition} =
    useSelector(selectDataStoreState);
  const {exhibitionFormModal} = useSelector(selectUiState);
  const [transit_type_val, setTransit_type_val] = useState("");

  const initialValues = {
    name: "",
    date_from: "",
    date_to: "",
    transit_type: "",
    exhibition_aggregate: "",
    exhibition_limit: "",
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required(MANDATORY_QUES),
      date_from: Yup.string().required(MANDATORY_QUES),
      date_to: Yup.string().required(MANDATORY_QUES),
      transit_type: Yup.string().required(MANDATORY_QUES),
      exhibition_aggregate: Yup.string(),
      exhibition_limit: Yup.string().required(MANDATORY_QUES),
    }),

    onSubmit: async (values, {resetForm}) => {
      const exhibitionLimit = normalizeCurrencyToNumber(
        values?.exhibition_limit
      );
      const exhibitionAggregate = normalizeCurrencyToNumber(
        values?.exhibition_aggregate
      );
      if (exhibitionLimit <= 0) {
        GlobalNotificationPopup({
          type: "error",
          message: "Exhibition limit must be greater than zero.",
        });
        return;
      }
      if (exhibitionLimit > exhibitionAggregate) {
        GlobalNotificationPopup({
          type: "error",
          message: "Exhibition limit can't exceed the aggregate value.",
        });
        return;
      }
      dispatch(setUiData({showLoader: true}));
      let data = {
        ...values,
        date_from: dayjs(values?.date_from).format("MM-DD-YYYY"),
        date_to: dayjs(values?.date_to).format("MM-DD-YYYY"),
        exhibition_aggregate: exhibitionAggregate,
        exhibition_limit: exhibitionLimit,
        proposal_id: exhibitionParentProposal?.proposal_id,
        ...(user?.user_type === "agent"
          ? {user_id: exhibitionParentProposal.user_id, agent_id: user.id}
          : {user_id: user.id}),
      };
      try {
        const response = await fetch(REQUEST_EXHIBITION.url, {
          method: REQUEST_EXHIBITION.method,
          headers: REQUEST_EXHIBITION.headers,
          body: JSON.stringify(data),
        });
        const res = await response.json();
        dispatch(setUiData({showLoader: false}));
        if (res.status == 1) {
          GlobalNotificationPopup({type: "success", message: res?.message});
          resetForm();
        } else {
          GlobalNotificationPopup({type: "error", message: res?.message});
        }
        handleCloseExhibitionForm();
      } catch (error) {
        handleCloseExhibitionForm();
        console.error("Error:", error);
        removeCookies("isLogin");
        dispatch(setUiData({showLoader: false}));
        GlobalNotificationPopup({
          type: "error",
          message: ERR_MESSAGE,
        });
        navigate("/");
      }
    },
  });

  const handleCloseExhibitionForm = (navigateToEndorse) => {
    fetchListAgain();
    formik.resetForm();
    // dispatch(setDataStore({
    //     exhibitionParentProposal: null
    //   }))
    setTransit_type_val("");
    dispatch(
      setDataStore({
        disableExhibition: false,
      })
    );
    dispatch(
      setUiData({
        exhibitionFormModal: false,
      })
    );
    if (navigateToEndorse) {
      dispatch(
        setDataStore({endorsementParentProposal: exhibitionParentProposal})
      );
      navigate("/endorsements");
    }
  };

  const getExhibitionCoverage = useCallback(
    async (value) => {
      if (value == null || value == "" || !user?.id) return;

      dispatch(setUiData({showLoader: true}));

      const data = {
        proposal_id: exhibitionParentProposal?.proposal_id,
        type: value,
        ...(user?.user_type === "agent"
          ? {agent_id: user.id}
          : {user_id: user.id}),
      };

      const apiDetails = GET_EXHIBITION_COVERAGE;

      const options = {
        method: apiDetails.method,
        headers: apiDetails.headers,
        body: JSON.stringify(data),
      };

      try {
        const response = await fetch(apiDetails.url, options);
        const res = await response.json();

        if (res.status == 1) {
          directSettingAmount(
            res?.data?.transits_limit ?? "",
            "exhibition_limit"
          );
          directSettingAmount(
            res?.data?.transits_aggregate_exposure ?? "",
            "exhibition_aggregate"
          );
          dispatch(
            setDataStore({
              disableExhibition:
                res?.data?.transits_aggregate_exposure == 0 ? true : false,
            })
          );
        } else {
          handleCloseExhibitionForm();

          GlobalNotificationPopup({
            type: "error",
            message: res?.message || "Failed to fetch Exhibitions.",
          });
        }
      } catch (error) {
        handleCloseExhibitionForm();
        removeCookies("isLogin");
        GlobalNotificationPopup({
          type: "error",
          message: error.message || "Something went wrong",
        });
        navigate("/");
      } finally {
        dispatch(setUiData({showLoader: false}));
      }
    },
    [exhibitionParentProposal, user]
  );

  useEffect(() => {
    if (transit_type_val) {
      getExhibitionCoverage(transit_type_val);
    }
  }, [transit_type_val]);

  const handleAmountFieldChange = (e, fieldName) => {
    const rawValue = e.target.value;
    const numericValue = parseToNumber(rawValue);
    formik.setFieldValue(
      fieldName,
      numericValue !== "" ? USD_FORMATTER.format(numericValue) : ""
    );
    // Delay setting error to ensure value is set first
    setTimeout(() => {
      if (parseFloat(numericValue) === 0) {
        formik.setFieldError(fieldName, "Amount must be greater than $0");
      }
    }, 0);
  };
  const directSettingAmount = (value, fieldName) => {
    const numericValue = value;
    formik.setFieldValue(
      fieldName,
      numericValue !== "" ? USD_FORMATTER.format(numericValue) : ""
    );
    // Delay setting error to ensure value is set first
    setTimeout(() => {
      if (parseFloat(numericValue) === 0) {
        formik.setFieldError(fieldName, "Amount must be greater than $0");
      }
    }, 0);
  };

  return (
    <>
      <GlobalModal
        open={exhibitionFormModal}
        handleClose={() => handleCloseExhibitionForm()}
        aria-labelledby="claim-history"
        closeIcon={true}
        title={"Exhibition Coverage"}
        subtitle={`Policy Number: ${exhibitionParentProposal?.policy_number}`}
        dialogTitleClass={style.claimPopTitle}
        // disableBackdropClick={true}
        dialogContent={
          <Grid container spacing={2}>
            <Grid size={{xs: 12, md: 12}} className={style.claimPopMain}>
              <form
                autoComplete="false"
                autoSave="false"
                onSubmit={formik.handleSubmit}
              >
                <Grid container spacing={2}>
                  <Grid
                    size={{xs: 12}}
                    // style={
                    //   disableExhibition
                    //     ? {opacity: "0.3", pointerEvents: "none"}
                    //     : {}
                    // }
                  >
                    <Grid container spacing={2}>
                      <Grid size={{xs: 12, md: 12}}>
                        <Box sx={{mb: 1, color: "#000"}}>
                          Name of Exhibition/Tradeshow*
                        </Box>
                        <GlobalTextField
                          id={"name"}
                          name={`name`}
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched?.name && Boolean(formik.errors?.name)
                          }
                          helperText={
                            formik.touched?.name && formik.errors?.name
                          }
                        />
                      </Grid>
                      <Grid size={{xs: 12, lg: 6, sm: 6}}>
                        <Box sx={{mb: 1, color: "#000"}}>Date From*</Box>
                        <GlobalDateField
                          name={`date_from`}
                          value={formik.values.date_from}
                          onChange={(newValue) =>
                            formik.setFieldValue(`date_from`, newValue)
                          }
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched?.date_from &&
                            Boolean(formik.errors?.date_from)
                          }
                          helperText={
                            formik.touched?.date_from &&
                            formik.errors?.date_from
                          }
                          disablePast
                          clearBtn={formik.values.date_from !== ""}
                        />
                      </Grid>
                      <Grid size={{xs: 12, lg: 6, sm: 6}}>
                        <Box sx={{mb: 1, color: "#000"}}>Date To*</Box>
                        <GlobalDateField
                          name={`date_to`}
                          value={formik.values.date_to}
                          dependentMinValue={formik.values.date_from}
                          onChange={(newValue) =>
                            formik.setFieldValue(`date_to`, newValue)
                          }
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched?.date_to &&
                            Boolean(formik.errors?.date_to)
                          }
                          helperText={
                            formik.touched?.date_to && formik.errors?.date_to
                          }
                          disablePast
                          disabled={!formik.values.date_from}
                          clearBtn={formik.values.date_to !== ""}
                        />
                      </Grid>
                      <Grid size={{xs: 12, md: 12}}>
                        <h3
                          className={style.htitleText}
                          style={{marginBottom: "8px"}}
                        >
                          Value of goods being shipped*
                        </h3>
                        <Grid container spacing={2} sx={{mt: 1.5}}>
                          {/* <Grid size={{ xs: 12, md: 12 }}>
                                                <Box sx={{ color: "#000" }}> Value of goods being shipped </Box>
                                            </Grid> */}
                          <Grid size={{xs: 12, lg: 6, sm: 6}}>
                            <GlobalRadio
                              title="Including Transit"
                              selected={
                                formik.values.transit_type === "including"
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                setTransit_type_val("including");
                                formik.setFieldValue(
                                  "transit_type",
                                  "including"
                                );
                              }}
                              error={
                                formik.touched.transit_type &&
                                Boolean(formik.errors.transit_type)
                              }
                              errorText={
                                formik.touched.transit_type &&
                                formik.errors.transit_type
                              }
                            />
                          </Grid>
                          <Grid size={{xs: 12, lg: 6, sm: 6}}>
                            <GlobalRadio
                              title="Excluding Transit"
                              selected={
                                formik.values.transit_type === "excluding"
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                setTransit_type_val("excluding");
                                formik.setFieldValue(
                                  "transit_type",
                                  "excluding"
                                );
                              }}
                              error={
                                formik.touched.transit_type &&
                                Boolean(formik.errors.transit_type)
                              }
                              errorText={
                                formik.touched.transit_type &&
                                formik.errors.transit_type
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={{xs: 12, lg: 6, sm: 6}}>
                        <Box sx={{mb: 1, color: "#000"}}>Aggregate</Box>
                        <GlobalTextField
                          name="exhibition_aggregate"
                          placeH={"$"}
                          value={formik.values?.exhibition_aggregate}
                          onChange={() => {}}
                          readOnly
                        />
                      </Grid>
                      <Grid size={{xs: 12, lg: 6, sm: 6}}>
                        <Box sx={{mb: 1, color: "#000"}}>Limit*</Box>
                        <GlobalTextField
                          name="exhibition_limit"
                          placeH={"$"}
                          value={formik.values?.exhibition_limit}
                          onChange={(e) =>
                            handleAmountFieldChange(e, `exhibition_limit`)
                          }
                          readOnly
                          disabled={disableExhibition}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{xs: 12, md: 12}} sx={{textAlign: "center"}}>
                    {disableExhibition ? (
                      <>
                        <Box sx={{textAlign: "left", mb: 1}}>
                          <span style={{color: "#FF2116", fontSize: 14}}>
                            Aggregate Exposure Not Available, Please Endorse the
                            Policy.
                          </span>
                        </Box>
                        <Button
                          className={style.claimPopBtn}
                          variant="contained"
                          autoFocus
                          type="button"
                          onClick={() => {
                            handleCloseExhibitionForm(true);
                          }}
                        >
                          Endorsement
                        </Button>
                      </>
                    ) : (
                      <Button
                        className={style.claimPopBtn}
                        variant="contained"
                        autoFocus
                        type="submit"
                      >
                        Submit
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        }
      ></GlobalModal>
    </>
  );
};

export default ExhibitionFormModal;
